'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { ServicesHeader } from '@/components/dashboard/services/ServicesHeader';
import {
  ServicesCategoryAccordion,
  ServiceItem,
  CategoryItem
} from '@/components/dashboard/services/ServicesCategoryAccordion';
import { ServiceTypeSelectModal } from '@/components/dashboard/services/ServiceTypeSelectModal';
import { ServiceDrawer } from '@/components/dashboard/services/ServiceDrawer';
import { CategoryModal } from '@/components/dashboard/services/CategoryModal';

export default function ServicesPage() {
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [staffList, setStaffList] = useState<{ id: string; name: string; colorHex: string; roleTitle?: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Modals state
  const [showTypeSelectModal, setShowTypeSelectModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingService, setEditingService] = useState<ServiceItem | null>(null);
  const [selectedModality, setSelectedModality] = useState<'CLASICO' | 'PAUSA' | 'ZONAS' | 'CONSULTA'>('CLASICO');

  const fetchData = useCallback(async () => {
    try {
      const [resServices, resStaff] = await Promise.all([
        fetch('/api/services'),
        fetch('/api/staff')
      ]);

      const dataServices = await resServices.json();
      const dataStaff = await resStaff.json();

      if (dataServices.success) {
        setServices(dataServices.services || []);
        setCategories(dataServices.categories || []);
      }

      if (dataStaff.success) {
        setStaffList(dataStaff.staff || []);
      }
    } catch (err) {
      console.error('Error fetching services & staff:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenNewService = () => {
    setEditingService(null);
    setShowTypeSelectModal(true);
  };

  const handleSelectModality = (
    modality: 'CLASICO' | 'PAUSA' | 'ZONAS' | 'CONSULTA',
    _templateName?: string
  ) => {
    setSelectedModality(modality);
    setShowTypeSelectModal(false);
    setShowEditModal(true);
  };

  const handleEditService = (service: ServiceItem) => {
    setEditingService(service);
    setSelectedModality((service.modality as any) || 'CLASICO');
    setShowEditModal(true);
  };

  const handleDeleteService = async (serviceId: string) => {
    if (!confirm('¿Estás seguro de que deseas archivar este servicio?')) return;

    try {
      const res = await fetch(`/api/services?id=${serviceId}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.success) {
        fetchData();
      } else {
        alert(data.error || 'Error al eliminar');
      }
    } catch (err) {
      alert('Error de conexión');
    }
  };

  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 2500);
  };

  const handleCategoryCreated = (newCat: any) => {
    setCategories((prev) => [...prev, newCat]);
    fetchData();
  };

  const handleReorderCategories = async (newCategories: CategoryItem[]) => {
    setCategories(newCategories);
    try {
      const items = newCategories.map((c, idx) => ({
        id: c.id,
        orderIndex: c.orderIndex ?? (idx + 1)
      }));

      const res = await fetch('/api/categories', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      const data = await res.json();
      if (data.success) {
        showToast('✓ Orden de categorías actualizado');
      } else {
        console.error('Error reordering categories:', data.error);
        fetchData();
      }
    } catch (err) {
      console.error('Error in handleReorderCategories:', err);
      fetchData();
    }
  };

  const handleReorderServices = async (newServices: ServiceItem[]) => {
    setServices(newServices);
    try {
      const items = newServices.map((s, idx) => ({
        id: s.id,
        orderIndex: s.orderIndex ?? (idx + 1),
        categoryId: s.categoryId || null
      }));

      const res = await fetch('/api/services', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      const data = await res.json();
      if (data.success) {
        showToast('✓ Orden de servicios actualizado');
      } else {
        console.error('Error reordering services:', data.error);
        fetchData();
      }
    } catch (err) {
      console.error('Error in handleReorderServices:', err);
      fetchData();
    }
  };

  const handleToggleFeaturedService = async (service: ServiceItem) => {
    const nextFeatured = !service.isFeatured;
    setServices((prev) =>
      prev.map((s) => (s.id === service.id ? { ...s, isFeatured: nextFeatured } : s))
    );
    try {
      const res = await fetch('/api/services', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: service.id,
          isFeatured: nextFeatured
        })
      });
      const data = await res.json();
      if (data.success) {
        showToast(nextFeatured ? '★ Servicio marcado como destacado' : 'Servicio quitado de destacados');
      } else {
        fetchData();
      }
    } catch (err) {
      fetchData();
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12 relative">
      {/* Toast Notifier */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 bg-slate-900/90 backdrop-blur-md text-white text-xs font-semibold px-4 py-2.5 rounded-full shadow-lg border border-slate-700/50 flex items-center gap-2 animate-in fade-in slide-in-from-bottom-2 duration-200">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Header */}
      <ServicesHeader
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onOpenNewService={handleOpenNewService}
      />

      {/* Main Services Table & Categories */}
      {loading ? (
        <div className="bg-white border border-slate-200/80 rounded-2xl p-12 text-center text-xs text-slate-400 font-medium">
          Cargando catálogo de servicios y categorías...
        </div>
      ) : (
        <ServicesCategoryAccordion
          categories={categories}
          services={services}
          searchQuery={searchQuery}
          onEditService={handleEditService}
          onDeleteService={handleDeleteService}
          onToggleFeaturedService={handleToggleFeaturedService}
          onEditCategory={() => setShowCategoryModal(true)}
          onReorderCategories={handleReorderCategories}
          onReorderServices={handleReorderServices}
        />
      )}

      {/* Step 1: Select Type or Template Modal */}
      <ServiceTypeSelectModal
        isOpen={showTypeSelectModal}
        onClose={() => setShowTypeSelectModal(false)}
        onSelectModality={handleSelectModality}
      />

      {/* Step 2: Configure Service Slide-Over Drawer */}
      <ServiceDrawer
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        service={editingService}
        modality={selectedModality}
        categories={categories}
        staffList={staffList}
        onSaved={fetchData}
        onOpenNewCategory={() => setShowCategoryModal(true)}
      />

      {/* New Category Modal */}
      <CategoryModal
        isOpen={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        onCategoryCreated={handleCategoryCreated}
      />
    </div>
  );
}
